import { Editor } from '@tinymce/tinymce-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GlobalCSSVariables } from '../../assets/GlobalStyle';
import { Colors } from '../../colors';
import { withFormikSupport } from '../../hocs/withFormikSupport';
import { InputUI } from '../../ui';
import { VALID_ELEMENTS, VALID_STYLES } from './constants';
import * as S from './styled';
import { getToolbar, mapValidElementsToString, mapValidStyles, setupInserter } from './utils';
const toolbarDefaults = {
    alignment: false,
    blocks: false,
    code: false,
    fullscreen: false,
    table: false,
    image: false,
    customImage: false,
};
const getContentStyle = (colorScheme) => {
    return `
    ${GlobalCSSVariables}

    html {
      min-height: 100%;
      cursor: text;
    }

    .mce-content-body {
      color-scheme: ${colorScheme};
      margin: 16px;
      font-family: "Suisse Int'l", Helvetica, Arial, sans-serif;
      font-size: 16px;
      line-height: 150%;
      color: ${Colors.GS1000};
      background-color: ${Colors.GS0};
    }

    .mce-content-body,
    .mce-content-body > *:first-child {
      margin-top: 8px;
    }

    .mce-content-body a {
      color: ${Colors.B500};
    }

    .mce-content-body [data-mce-selected=inline-boundary] {
      background: ${Colors.B200};
    }

    .mce-content-body img {
      display: block;
      margin: 1em auto;
      max-width: 100%;
      height: auto;
    }

    .mce-content-body[data-mce-placeholder]:not(.mce-visualblocks)::before {
      color: ${Colors.GS600};
    }
  `;
};
export const TinyMCE = withFormikSupport(({ tinymceScriptSrc, placeholder, resize = false, statusbar = false, buttons, inserterOptions, height = 160, name = '', initialValue, value, disabled = false, invalid = false, error, submitCount, invalidElements, imagesUploadHandler, appearance = 'light', onChange, onValueChange, onSetup, onBlur, }) => {
    const touchedRef = useRef(false);
    const [focused, setFocused] = useState(false);
    useEffect(() => {
        // NOTE: reset touched state after each form reset to prevent initial value encoding
        if (!focused && value === initialValue) {
            touchedRef.current = false;
        }
    }, [value, initialValue, focused]);
    const init = useMemo(() => ({
        plugins: 'lists link table code fullscreen autoresize autolink',
        menubar: false,
        toolbar: getToolbar({ ...toolbarDefaults, ...buttons }),
        toolbar_mode: 'wrap',
        contextmenu: false,
        elementpath: false,
        max_height: 800,
        min_height: 160,
        branding: false,
        resize,
        statusbar: statusbar || resize,
        setup: (editor) => {
            setupInserter(editor, inserterOptions);
            // FIX for https://popshop.atlassian.net/browse/PRD-2381
            // solution: https://stackoverflow.com/a/67166716/7518346
            editor.on('paste cut', () => {
                editor.setDirty(true); // this updates the editor.undoManager
            });
            onSetup?.(editor);
        },
        placeholder,
        width: '100%',
        height,
        content_style: getContentStyle(appearance),
        valid_styles: mapValidStyles(VALID_STYLES),
        valid_elements: mapValidElementsToString(VALID_ELEMENTS),
        relative_urls: false,
        invalid_elements: invalidElements,
        images_upload_handler: imagesUploadHandler,
        image_dimensions: false,
        paste_data_images: false,
        object_resizing: false,
        remove_script_host: false,
        link_assume_external_targets: 'https',
    }), [buttons, height, placeholder, inserterOptions, onSetup, resize, statusbar, appearance]);
    const handleEditorChange = useCallback((newValue) => {
        // NOTE: handleEditorChange is called before useEffect which sets touchedRef.current to false so we need to
        // prevent encoding entities by TinyMCE after form is being reset
        if (!focused && value === initialValue)
            return;
        if (!touchedRef.current)
            return;
        if (onChange && name)
            onChange({ target: { name, value: newValue } });
        if (onValueChange)
            onValueChange(newValue);
    }, [focused, name, value, initialValue, onChange, onValueChange]);
    const handleFocus = useCallback(() => {
        if (!touchedRef.current)
            touchedRef.current = true;
        setFocused(true);
    }, []);
    const handleBlur = useCallback((event, editor) => {
        setFocused(false);
        const value = editor.getContent({ format: 'html' });
        if (onBlur)
            onBlur({ target: { name, value } });
    }, [onBlur, name]);
    const renderDarkModeGlobalStyle = () => {
        if (appearance === 'light dark' &&
            window.matchMedia('(prefers-color-scheme: dark)').matches) {
            return React.createElement(S.DarkModeGlobalStyle, null);
        }
        if (appearance === 'dark') {
            return React.createElement(S.DarkModeGlobalStyle, null);
        }
        return null;
    };
    return (React.createElement(S.Container, { "data-testid": "TinyMCE" },
        renderDarkModeGlobalStyle(),
        React.createElement(InputUI.Box, { "$fullHeight": true, "$disabled": disabled, "$focused": focused, "$invalid": invalid },
            React.createElement(Editor, { tinymceScriptSrc: tinymceScriptSrc, init: init, initialValue: initialValue, value: value, disabled: disabled, onEditorChange: handleEditorChange, onFocus: handleFocus, onBlur: handleBlur })),
        !!error && React.createElement(InputUI.Error, { errorTimestampKey: submitCount }, error)));
});
